import { Injectable } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { AbstractControl, ValidatorFn } from '@angular/forms';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { Amplify, Auth } from 'aws-amplify';
//import * as AWS from 'aws-sdk';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { environment } from 'src/environments/environment';
import { ThemingService, ShopTheming } from './theming.service';
import { UserPoolService } from './userPool.service';
import { LambdaService } from './lambda.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private region = '';
  private userPoolId = '';
  private permissions: any = null;
  private users: any[] = [];
  public shopTheme: ShopTheming[] = [];

  private currentUserEmail: string | null = null;
  private currentPassword: string | null = null;
  public shopName: string = '';
  // Subjects para notificar cambios
  private userEmailSubject = new BehaviorSubject<string | null>(null);
  private passwordSubject = new BehaviorSubject<string | null>(null);

  // Observables públicos para que los componentes se suscriban
  public userEmail$ = this.userEmailSubject.asObservable();
  public password$ = this.passwordSubject.asObservable();

  constructor(public themingService: ThemingService, public router: Router, private userPoolService: UserPoolService, private lambdaService: LambdaService) {}

  /**
   * The function is called initialize and it configures the Amplify library to use the AWS Cognito
   * User Pool that was created in the AWS Console.
   */

  initialize(): void {
    this.configureAmplify(window.location.pathname);
  }

  configureAmplify(pathname: string): void {

    this.region = 'us-east-1'
    this.userPoolService.getUserPoolId().then((data)=>{
      this.userPoolId = data.poolId;
      Amplify.configure({
        Auth: {
          region: this.region,
          userPoolId: data.poolId,
          userPoolWebClientId: data.clientId,
          mandatorySignIn: true
        }
      })
    })
    .catch((err) => {
      console.error(err);
    })
    // AWS.config.region = this.region;
    // AWS.config.update({
    //   accessKeyId: this.themingService.lambdaService.ID,
    //   secretAccessKey: this.themingService.lambdaService.SECRET,
    // });

    this.currentAuthUser().then((result) => {
      if(!result || !this.themingService.shopGuid){
        this.onSignOut().then(()=>{
          this.themingService.setShopId(0,"")
          this.router.navigate(['/login']);
        })
      }
    }).catch((err) => {
      if(err == "The user is not authenticated"){
        console.log(err);
        this.router.navigate(['/login']);
      }else{
        console.error(err);
      }
    });

    // if (this.themingService.shopTheming[0] == undefined) {
    //   setTimeout(() => {
    //     this.configureAmplify(pathname);
    //   }, 100);
    // } else {
    //   let amplifyConfigBackoffice = JSON.parse(
    //     this.themingService.shopTheming[0].amplifyConfigBackoffice
    //   );
    //   this.shopName = this.themingService.shopTheming[0].shopName;
    //   this.region = amplifyConfigBackoffice.region;
    //   this.userPoolId = amplifyConfigBackoffice.userPoolId;

    //   Amplify.configure({
    //     Auth: amplifyConfigBackoffice,
    //     storage: sessionStorage,
    //   });

    //   AWS.config.region = this.region;
    //   AWS.config.update({
    //     accessKeyId: this.themingService.apiService.ID,
    //     secretAccessKey: this.themingService.apiService.SECRET,
    //   });

      this.currentAuthUser()
        .then(() => {
          this.router.navigate([pathname]);
        })
        .catch(() => {
          this.router.navigate(['/login']);
        });
    // }
  }

  /**
   * This function takes in a given email and password, and then signs up a user with that email and
   * password.
   * @param {string} givenEmail - The email address of the user.
   * @param {string} givenPassowrd - string - The password that the user entered
   */
  public async onSignUp(givenEmail: string, givenPassowrd: string) {
    await Auth.signUp({
      username: givenEmail,
      password: givenPassowrd,
    });
  }

  /**
   * "This function takes in a given email and password, and returns a promise that will resolve to the
   * result of the signIn function from the AWS Amplify Auth module."
   *
   * The above function is a good example of how to use the AWS Amplify Auth module.
   *
   * The AWS Amplify Auth module is a JavaScript library that allows you to integrate AWS Cognito into
   * your JavaScript application.
   *
   * The AWS Amplify Auth module has a signIn function that takes in an object with a username and
   * password property.
   *
   * The signIn function returns a promise that will resolve to the result of the signIn function.
   *
   * The result of the signIn function is an object with a user property.
   *
   * The user property is an object with a username property.
   *
   * The username property is a string that contains the username of the user that was signed in.
   *
   * The username
   * @param {string} givenEmail - The email address of the user.
   * @param {string} givenPassword - string - The password the user entered
   * @returns The return value is a promise.
   */

  // ✅ MFA app (TOTP)

public async setupTotp(user: any): Promise<string> {
  try {
    const secretCode = await Auth.setupTOTP(user);
    const issuer = this.themingService.shopTheming[0].shopName || 'Mobydyg';

    const name = user.attributes?.given_name.trim() || '';
    const last = user.attributes?.family_name.trim() || '';
    const accountName = encodeURIComponent(`${name}.${last}` || user.username);

    const otpAuthUri = `otpauth://totp/${issuer}:${accountName}?secret=${secretCode}&issuer=${encodeURIComponent(issuer)}`;
    //console.log('otpUri:', otpAuthUri);
    return otpAuthUri;
  } catch (err) {
    console.error('Error configurando TOTP:', err);
    throw err;
  }
}

public async verifyTotp(user: any, code: string): Promise<void> {
  try {
    await Auth.verifyTotpToken(user, code);
    await Auth.setPreferredMFA(user, 'TOTP');
  } catch (err) {
    console.error('Error verificando TOTP:', err);
    throw err;
  }
}

public async confirmMfaCode(user: any, code: string, challengeName: string) {
  try {
    const result = await Auth.confirmSignIn(user, code, challengeName as 'SOFTWARE_TOKEN_MFA' | 'SMS_MFA');
    return { status: 'SUCCESS', user: result };
  } catch (err) {
    console.error('Error al confirmar MFA:', err);
    throw err;
  }
}

async disableTotp(user: CognitoUser): Promise<void> {
  await Auth.setPreferredMFA(user, 'NOMFA');
}


public async onLogIn(givenEmail: string, givenPassword: string) {
  try {
    //console.log('onLogIn:', givenEmail, givenPassword);
    const result = await Auth.signIn({
      username: givenEmail,
      password: givenPassword,
    });

    this.currentUserEmail = givenEmail;
    this.currentPassword = givenPassword;
    this.userEmailSubject.next(givenEmail);
    this.passwordSubject.next(givenPassword);

    if (result.challengeName === 'SOFTWARE_TOKEN_MFA') {
      console.log(' onLogIn MFA required', result);
      return {
        status: 'MFA_REQUIRED',
        user: result,
        challenge: 'SOFTWARE_TOKEN_MFA'
      };
    }

    if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
      return {
        status: 'NEW_PASSWORD_REQUIRED',
        user: result
      };
    }

    return {
      status: 'SUCCESS',
      user: result
    };

  } catch (error) {
    console.error('Login failed:', error);
    throw error;
  }
}

  public getCurrentUserEmail(): string | null {
    return this.currentUserEmail;
  }

  public getCurrentPassword(): string | null {
    return this.currentPassword;
  }

  public async getCurrentUserDetails() {
    const user = await this.currentAuthUser();
    const payload = user?.signInUserSession?.idToken?.payload || {};

    return {
      email: this.currentUserEmail,
      username: user.username,
      password: this.currentPassword,
      attributes: user.attributes,
      given_name: user.attributes?.given_name,
      family_name: user.attributes?.family_name,
      sub: payload['sub'],
      role: payload['custom:user_role'] || user.attributes?.['custom:user_role'],
    };
  }

  /**
   * It returns the current authenticated user, bypassing the cache
   * @returns The current authenticated user.
   */
  public async currentAuthUser() {
    const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
    return user;
  }

  public async getUserRole(): Promise<string> {
    try {
      const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
      const userRole = user.attributes['custom:user_role'];
      return userRole;
    } catch (error) {
      console.error('Error al obtener el rol del usuario:', error);
      throw error;
    }
  }

  public async getUserPermissions() {
    if (this.permissions) {
      return this.permissions;
    }
    try {
      const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
      const isOwner = user.signInUserSession.accessToken.payload['cognito:groups']?.includes('Owner') ||
                      user.attributes['custom:user_role'] === 'Owner' || user.attributes['custom:user_role'] === 'Admin';
      let permissions = {
        home: false,
        sales: false,
        cashRegisters: false,
        products: false,
        giftCards: false,
        coupons: false,
        clients: false,
        calendar: false,
        config: false,
        generalInfo: false,
        userRoles: false,
        categories: false,
        collections: false,
        supliers: false,
        shopPoints: false,
        paymentMethods: false,
        themingService: false,
        faqs: false,
        exportClients: false,
        pos: false,
        pages: false,
        analytics: false,
        policies: false,
        emailTemplate: false,
        shippingCosts: false,
        chatApp: false,
      };

      if (isOwner) {
        permissions = {
          home: true,
          sales: true,
          cashRegisters: true,
          products: true,
          giftCards: true,
          coupons: true,
          clients: true,
          calendar: true,
          config: true,
          generalInfo: true,
          userRoles: true,
          categories: true,
          collections: true,
          supliers: true,
          shopPoints: true,
          paymentMethods: true,
          themingService: true,
          faqs: true,
          exportClients: true,
          pos: true,
          pages: true,
          analytics: true,
          policies: true,
          emailTemplate: true,
          shippingCosts: true,
          chatApp: true,
        };
      } else {
        const permissionsAttr = user.attributes['custom:permissions'];
        if (permissionsAttr) {
          permissions = JSON.parse(permissionsAttr);
        }
      }
      this.permissions = permissions;
      return permissions;
    } catch (error) {
      console.error('Error obteniendo los permisos del usuario', error);
      throw error;
    }
  }
  /**
   * This function is called when the user has signed up with a new password and the user has been
   * authenticated
   * @param {any} user - The user object that was returned by the Auth.signIn() method.
   * @param {string} newPassword - The new password that the user has entered.
   */
  public async onNewPasswordRequired(user: any, newPassword: string) {
    await Auth.completeNewPassword(user, newPassword);
  }

  /**
   * This function will resend the confirmation code to the user's email address.
   * @param {string} givenEmail - The email address of the user who needs a new confirmation code.
   */
  public async resendConfirmationCode(givenEmail: string) {
    await Auth.resendSignUp(givenEmail);
  }

  /**
   * This function takes a given email, and sends a password reset email to that email address.
   * @param {string} givenEmail - The email address of the user who has forgotten their password.
   */
  public async forgotPassword(givenEmail: string) {
    await Auth.forgotPassword(givenEmail);
  }

  /**
   * "This function takes in a given email, a given code, and a given password, and then it submits the
   * given email, the given code, and the given password to the AWS Cognito forgotPasswordSubmit
   * function."
   * </code>
   * @param {string} givenEmail - The email address of the user who is changing their password.
   * @param {string} givenCode - The code sent to the user via email or other delivery mechanism.
   * @param {string} givenPassword - The new password.
   */
  public async changePassword(
    givenEmail: string,
    givenCode: string,
    givenPassword: string
  ) {
    await Auth.forgotPasswordSubmit(givenEmail, givenCode, givenPassword);
  }

  /**
   * This function will sign the user out of the application and redirect them to the sign in page.
   */
  public async onSignOut() {
    await Auth.signOut({ global: true });
    //borrar el cookie de shopGuid
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate());
    document.cookie = `shopGuid=0; expires=${expirationDate.toUTCString()};`
  }

  /**
   * It returns a promise that resolves to an array of users.
   * @returns The users array is being returned.
   */
  public async getUsers() {

    await this.lambdaService.cognitoInstruction('listUsers', {
      userPoolId: this.userPoolId
    }).then((users: any) => {
      this.users = users;
    });
    return this.users;

  }

  /**
   * It takes a username as a parameter, and returns a promise that resolves to an object containing the
   * user's data.
   * @param {string} username - The username of the user you want to get information about.
   * @returns The user data is being returned.
   */
  public async getUser(username: string) {
    let userdata;
    await this.lambdaService.cognitoInstruction('getUser', {
      userPoolId: this.userPoolId,
      username
    }).then((user: any) => {
      userdata = user;
    });

    return userdata;

  }

  public async listGroups() {
    let groupsData;
    await this.lambdaService.cognitoInstruction('listGroups', {
      userPoolId: this.userPoolId
    }).then((groups: any) => {
      groupsData = groups
    });

    return groupsData;

  }

  public async adminAddUserToGroup(username: string, groupName: string) {

    await this.lambdaService.cognitoInstruction('adminAddUserToGroup', {
      userPoolId: this.userPoolId,
      username: username,
      groupName: groupName
    }).then((response: any) => {
      console.log('Resultado:', response.message); // "Usuario asignado al grupo correctamente"
    });
  }

  public async createUserAdmin(
    given_name: string,
    family_name: string,
    email: string,
    user_role: string,
    temporary_password: string,
    permissions: any,
    shop_access: string
  ) {

    let resp;
    await this.lambdaService.cognitoInstruction('adminCreateUser', {
      userPoolId: this.userPoolId,
      email: email,
      given_name: given_name,
      family_name: family_name,
      user_role: user_role,
      temporary_password: temporary_password, // debe cumplir política de Cognito
      shop_access: shop_access,
      permissions: permissions
    }).then((response: any) => {
      console.log('Usuario creado:', response);
      resp = response;
    });

    return resp;

  }

  public async deleteUser(username: string) {

    await this.lambdaService.cognitoInstruction('adminDeleteUser', {
      userPoolId: this.userPoolId,
      username: username
    }).then((response: any) => {
      console.log('Resultado:', response.message); // "Usuario eliminado exitosamente"
    });
  }

  public async disableUserAccess(username: string) {

    await this.lambdaService.cognitoInstruction('adminDisableUser', {
      userPoolId: this.userPoolId,
      username: username
    }).then((response: any) => {
      console.log('Resultado:', response.message); // "Usuario deshabilitado exitosamente"
    });


  }

  public async enableUserAccess(username: string) {

    this.lambdaService.cognitoInstruction('adminEnableUser', {
      userPoolId: this.userPoolId,
      username: username
    }).then((response: any) => {
      console.log('Resultado:', response.message); // "Usuario deshabilitado exitosamente"
    });

  }

  public async updateUser(
    username: string,
    given_name: string,
    family_name: string,
    phone_number: string | null,
    permissions: any,
    shop_access: string
  ) {

    this.lambdaService.cognitoInstruction('adminUpdateUserAttributes', {
      userPoolId: this.userPoolId,
      username,
      given_name,
      family_name,
      phone_number,
      shop_access,
      permissions
    }).then((response: any) => {
      console.log('Resultado:', response.message); // "Usuario actualizado exitosamente"
    });


  }


  public async updatePassword(oldPassword: string, newPassword: string) {

      const user = await Auth.currentAuthenticatedUser();
      return Auth.changePassword(user, oldPassword, newPassword);
    // let params = {
    //   AccessToken: (await Auth.currentSession()).getAccessToken().getJwtToken(),
    //   PreviousPassword: oldPassword,
    //   ProposedPassword: newPassword,
    // };
    // return new Promise((resolve, reject) => {
    //   let cognitoidentityserviceprovider =
    //     new AWS.CognitoIdentityServiceProvider();
    //   cognitoidentityserviceprovider.changePassword(params, (err, data) => {
    //     if (err) {
    //       reject(err);
    //     } else {
    //       resolve(data);
    //     }
    //   });
    // });
  }

  /**
   * It handles errors.
   * @param {String} message - String, givenEmail: string
   * @param {string} givenEmail - string
   */
  handleErrors(message: String, givenEmail: string) {
    switch (message) {
      case 'User does not exist.':
        return 'El usuario no está registrado';

      case 'Incorrect username or password.':
        return 'El nombre de usuario o contraseña es incorrecto';

      case 'User is not confirmed.':
        this.resendConfirmationCode(givenEmail);
        return 'Por favor, verifique su cuenta con el enlace enviado a su correo';

      case 'An account with the given email already exists.':
        return 'Este correo ya está registrado, inicia sesión para continuar';

      case 'Pending sign-in attempt already in progress':
        return 'Ya hay un intento de inicio de sesión pendiente en progreso';

      case 'Username/client id combination not found.':
        return 'Este correo no posee una cuenta registrada';

      case 'Attempt limit exceeded, please try after some time.':
        return 'Se excedió el limite de intentos, intenta en otro momento';

      case 'Cannot reset password for the user as there is no registered/verified email or phone_number':
        this.resendConfirmationCode(givenEmail);
        return 'Por favor, revisa tu correo y verifica tu cuenta antes de continuar';

      case 'Invalid verification code provided, please try again.':
        return 'Su código de verificación es incorrecto';

      case 'Invalid code provided, please request a code again.':
        return 'Código de verificación incorrecto, solicita un nuevo código';

      case 'Password attempts exceeded':
        return 'Intentos de contraseña excedidos, cambie su contraseña';

      case 'PreAuthentication failed with error Cannot authenticate users without admin role.':
        return 'Usuario no autorizado';

      case 'Temporary password has expired and must be reset by an administrator.':
        return 'La contraseña temporal ha expirado y debe ser reseteada por un administrador';

      case 'User password cannot be reset in the current state.':
        return 'La contraseña no puede ser cambiada en este estado';

      case 'An account with the given email already exists.backoffice':
        return 'Este usuario ya está registrado en tu tienda';

      case 'User is disabled.':
        return 'El usuario está deshabilitado';

      default:
        return message;
    }
  }

  /**
   * If the password and confirm password fields are not equal, set the confirm password field to have
   * an error of matching: true.
   * @param {string} password - string - The name of the password field
   * @param {string} ConfirmPassword - The name of the control that you want to validate.
   * @returns a validator function.
   */
  public confirmPasswordValidator(
    password: string,
    ConfirmPassword: string
  ): ValidatorFn {
    return (controls: AbstractControl) => {
      const control = controls.get(password);
      const checkControl = controls.get(ConfirmPassword);
      if (checkControl?.errors && !checkControl.errors['matching']) {
        return null;
      }
      if (control?.value !== checkControl?.value) {
        controls.get(ConfirmPassword)?.setErrors({ matching: true });
        return { matching: true };
      } else {
        return null;
      }
    };
  }
}

/* An animation. */
export const alertsAnimation = trigger('inOutAnimation', [
  transition(':enter', [
    style({ opacity: 0 }),
    animate('100ms', style({ opacity: 1 })),
  ]),
  transition(':leave', [animate('0ms', style({ opacity: 0 }))]),
]);

/* If the user is authenticated, return true, otherwise navigate to the home page and return false. */
@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(private router: Router, private authService: AuthService) {}
  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return Auth.currentAuthenticatedUser()
      .then(() => {
        return true;
      })
      .catch(() => {
        return false;
      });
  }
}
/* If the user is authenticated and is a member of the Owner group, then the user can access the route.
Otherwise, the user is redirected to the login page. */
@Injectable({
  providedIn: 'root',
})
export class AuthGuardOwner implements CanActivate {
  constructor(private router: Router) {}
  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return Auth.currentAuthenticatedUser()
      .then((user) => {
        if (
          user.signInUserSession.accessToken.payload['cognito:groups'].includes(
            'Owner'
          ) ||
          user.attributes['custom:user_role'] == 'Owner'
        ) {
          return true;
        } else {
          return false;
        }
      })
      .catch(() => {
        return false;
      });
  }
}

/* If the user is authenticated, redirect to the my-shops route, otherwise allow the user to continue
to the route they are trying to access.
</code> */
@Injectable({
  providedIn: 'root',
})
export class UnauthGuard implements CanActivate {
  constructor(private router: Router) {}
  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return Auth.currentAuthenticatedUser()
      .then(() => {
        return false;
      })
      .catch((user) => {
        return true;
      });
  }
}

@Injectable({
  providedIn: 'root',
})
export class AuthGuardPermission implements CanActivate {
  constructor(private authService: AuthService, private router: Router) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.authService.currentAuthUser()
      .then((user) => {
        return this.authService.getUserPermissions()
          .then(async (permissions) => {
            const requiredPermission = next.data['permission'] as string;
            const userRole = await this.authService.getUserRole();

            if (userRole === 'Owner' || userRole === 'Admin') {
              return true;
            }

            if ((permissions as { [key: string]: boolean })[requiredPermission]) {
              return true;
            } else {
              this.router.navigate(['home']);
              return false;
            }
          });
      })
      .catch((error) => {
        console.error('Error capturado:', error);
        if (error === 'not authenticated' || error?.message?.includes('User is not authenticated')) {
          this.router.navigate(['/home']);
        } else {
          console.error('Otro tipo de error:', error);
        }

        return false;
      });

  }
}



